// ============================================================================
// Voice Command Types and Mappings
// Dynamically generated from centralized route definitions
// ============================================================================

import { VOICE_ROUTES, VoiceRouteDefinition } from '../../routes/routeDefinitions';
import type { VoiceCommand, NormalizedCommand } from './types';

// Re-export types for backward compatibility
export type { VoiceCommand, NormalizedCommand } from './types';

// ============================================================================
// Dynamic Voice Commands Generation from Centralized Routes
// ============================================================================

/**
 * Convert centralized route definitions to voice commands
 */
function generateVoiceCommandsFromRoutes(routes: VoiceRouteDefinition[]): VoiceCommand[] {
  return routes.map(route => ({
    keywords: route.keywords,
    route: route.path,
    label: route.label,
    category: route.category,
    requiresPatientId: route.requiresPatientId,
    fallbackPath: route.fallbackPath,
  }));
}

// Generate voice commands from centralized route definitions
export const VOICE_COMMANDS: VoiceCommand[] = generateVoiceCommandsFromRoutes(VOICE_ROUTES);

// ============================================================================
// Similarity Matching Configuration
// ============================================================================

const SIMILARITY_THRESHOLD = 0.6;

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;
  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }
  return dp[m][n];
}

/**
 * Calculate similarity score between two strings (0 to 1)
 */
function calculateSimilarity(str1: string, str2: string): number {
  const maxLen = Math.max(str1.length, str2.length);
  if (maxLen === 0) return 1;
  const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase());
  return 1 - distance / maxLen;
}

/**
 * Check if input contains keyword
 */
function containsKeyword(input: string, keyword: string): boolean {
  const normalizedInput = input.toLowerCase().trim();
  const normalizedKeyword = keyword.toLowerCase().trim();
  return normalizedInput.includes(normalizedKeyword);
}

/**
 * Check if voice navigation is enabled by user preference (stored in localStorage)
 * @returns true if voice navigation is enabled, false otherwise
 */
export function isVoiceNavigationEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  const stored = localStorage.getItem('voiceNavigationEnabled');
  // Default to true if not set
  return stored === null ? true : stored !== 'false';
}

/**
 * Normalize and match voice input to commands
 * Prioritizes longer keyword matches to avoid partial matches
 */
export function normalizeVoiceCommand(input: string): NormalizedCommand | null {
  // Check if voice navigation is disabled by user
  if (!isVoiceNavigationEnabled()) return null;

  if (!input || input.trim().length === 0) return null;

  const normalizedInput = input.toLowerCase().trim()
    .replace(/go to /g, '')
    .replace(/open /g, '')
    .replace(/show /g, '')
    .replace(/navigate to /g, '')
    .replace(/take me to /g, '');

  // First, collect all potential matches with their scores and keyword lengths
  const allMatches: Array<{
    command: VoiceCommand;
    keyword: string;
    score: number;
    keywordLength: number;
    isExact: boolean;
  }> = [];

  for (const command of VOICE_COMMANDS) {
    for (const keyword of command.keywords) {
      const normalizedKeyword = keyword.toLowerCase();
      
      // Exact match - highest priority
      if (normalizedInput === normalizedKeyword) {
        allMatches.push({
          command,
          keyword,
          score: 1,
          keywordLength: keyword.length,
          isExact: true,
        });
        continue;
      }

      // Contains match - prioritize longer keywords
      if (containsKeyword(normalizedInput, keyword)) {
        const score = keyword.length / normalizedInput.length;
        allMatches.push({
          command,
          keyword,
          score: Math.min(score, 0.95),
          keywordLength: keyword.length,
          isExact: false,
        });
        continue;
      }

      // Fuzzy match
      const similarity = calculateSimilarity(normalizedInput, keyword);
      if (similarity > SIMILARITY_THRESHOLD) {
        allMatches.push({
          command,
          keyword,
          score: similarity,
          keywordLength: keyword.length,
          isExact: false,
        });
      }
    }
  }

  if (allMatches.length === 0) return null;

  // Sort by: 1) exact match first, 2) longer keyword length, 3) higher score
  allMatches.sort((a, b) => {
    // Exact matches always win
    if (a.isExact && !b.isExact) return -1;
    if (!a.isExact && b.isExact) return 1;
    
    // For non-exact matches, prefer longer keywords (more specific)
    if (a.keywordLength !== b.keywordLength) {
      return b.keywordLength - a.keywordLength;
    }
    
    // If same length, prefer higher score
    return b.score - a.score;
  });

  const bestMatch = allMatches[0];
  
  return {
    command: bestMatch.keyword,
    route: bestMatch.command.route,
    label: bestMatch.command.label,
    confidence: bestMatch.score,
    requiresConfirmation: bestMatch.command.requiresConfirmation || false,
    requiresPatientId: bestMatch.command.requiresPatientId,
    fallbackPath: bestMatch.command.fallbackPath,
    category: bestMatch.command.category,
  };
}

/**
 * Get command suggestions based on partial input
 */
export function getCommandSuggestions(input: string, limit = 5): VoiceCommand[] {
  // Return empty if voice navigation is disabled by user
  if (!isVoiceNavigationEnabled()) return [];

  if (!input || input.trim().length < 2) {
    return VOICE_COMMANDS.slice(0, limit);
  }

  const normalizedInput = input.toLowerCase().trim();
  const scored = VOICE_COMMANDS.map(command => {
    const maxScore = Math.max(
      ...command.keywords.map(keyword => {
        if (keyword.toLowerCase().includes(normalizedInput)) {
          return 0.9;
        }
        return calculateSimilarity(normalizedInput, keyword);
      })
    );
    return { command, score: maxScore };
  });

  return scored
    .filter(item => item.score > 0.3)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(item => item.command);
}

// ============================================================================
// Routes where Voice Navigation should be disabled
// ============================================================================

const DISABLED_VOICE_NAVIGATION_ROUTES = [
  '/ai/aihub/voicedataentry/chat',
];

/**
 * Check if voice navigation should be disabled for the given pathname
 * @param pathname - Current route pathname (from useLocation().pathname)
 * @returns true if voice navigation should be disabled
 */
export function isVoiceNavigationDisabled(pathname: string): boolean {
  // Check user preference first
  if (!isVoiceNavigationEnabled()) return true;

  const normalizedPath = pathname.toLowerCase().replace(/\/$/, '');
  return DISABLED_VOICE_NAVIGATION_ROUTES.some(
    route => normalizedPath === route || normalizedPath.startsWith(route + '/')
  );
}
